import {
    ADYEN_SCRIPT_INTEGRITY,
    ADYEN_SCRIPT_URL,
    ADYEN_STYLESHEET_INTEGRITY,
    ADYEN_STYLESHEET_URL,
    CardPaymentSubmissionFailure,
    CardPaymentSubmissionResult,
    IntegratedPaymentProvider,
    PaymentAuthorisationType,
    PaymentCard,
    PaymentType,
    STRIPE_SCRIPT_URL,
    ThreeDSResult,
    TYRO_SCRIPT_URL,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult,
    WalletProvider
} from './payment';
import {
    CustomerDetails,
    CustomerDetailsService,
    PaymentCustomerDetails
} from '../customer-info/customer-details.service';
import {computed, effect, inject, Injectable, Signal, signal, WritableSignal} from "@angular/core";
import {ResponsiveService} from "../responsive/responsive.service";
import {AnalyticsService} from '../logging/analytics.service';
import {NavigationService} from "../navigation/navigation.service";
import {ConfigurationModel} from "../configuration/configuration.model";
import {LoadingService} from "../ui-elements/loading/loading.service";
import {BehaviorSubject, debounceTime, Subject} from "rxjs";
import {onEach, whenObservableMatches} from '../utils/observable-utils';
import {OrderType} from '../order/order';
import {PaymentAuthorisationData} from '../order/order-placement.service';
import {OrderInitiationStatus, OrderModel, OrderSubmissionStatus} from '../order/order.model';
import {OrderPaymentDetailsData, OrderPaymentDetailsEntityModel} from "./order-payment-details-entity.model";
import {NotificationService} from "../notification/notification.service";
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {EntityData} from "@fedelta-pos/data-client";
import {WalletPaymentProcessor} from "./wallet-payment/wallet-payment-processor";
import {StripeWalletPaymentProcessor} from "./wallet-payment/stripe-wallet-payment-processor";
import {TyroWalletPaymentProcessor} from "./wallet-payment/tyro-wallet-payment-processor";
import {AdyenWalletPaymentProcessor} from "./wallet-payment/adyen-wallet-payment-processor";
import {AuthorisedTapToPayPayment, TapToPayService} from "./tap-to-pay/tap-to-pay.service";
import {AppDeviceErrorType, isAppDeviceError} from '../app-device/app-device.service';
import {PaymentSurchargeData} from '../order/order-initiation.service';
import {effectOnSignalSet} from '../utils/signal-utils';
import {DeferredInjectable} from '../deferred-injection.service';
import {GroupOrderModel} from '../group-order/group-order.model';
import {OrderingPhase, SessionModel} from '../session/session.model';
import {uuid} from '../utils/string-utils';
import {ExternalScriptLoaderService} from '../external-api/external-script-loader.service';

@Injectable({providedIn: "root"})
export class PaymentModel implements DeferredInjectable {
    public navigationService: NavigationService = inject(NavigationService);
    public analyticsService: AnalyticsService = inject(AnalyticsService);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public sessionModel: SessionModel = inject(SessionModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);
    public loadingService: LoadingService = inject(LoadingService);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderPaymentDetailsModel: OrderPaymentDetailsEntityModel = inject(OrderPaymentDetailsEntityModel);
    public customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);
    public notificationService: NotificationService = inject(NotificationService);
    private tapToPayService: TapToPayService = inject(TapToPayService);
    private externalScriptLoaderService: ExternalScriptLoaderService = inject(ExternalScriptLoaderService);
    private orderModel: OrderModel;
    private groupOrderModel: GroupOrderModel;

    private static readonly SHOW_WALLET_PAYMENT_SHEET_EVENT: string = "showWalletPaymentSheet";

    private readonly _paymentProvider: WritableSignal<IntegratedPaymentProvider> = signal<IntegratedPaymentProvider>(undefined);
    public readonly paymentProvider: Signal<IntegratedPaymentProvider> = this._paymentProvider.asReadonly();
    private readonly _paymentPlatformPublicKey: WritableSignal<string> = signal<string>(undefined);
    public readonly paymentPlatformPublicKey: Signal<string> = this._paymentPlatformPublicKey.asReadonly();
    private readonly _paymentPlatformLiveMode: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly paymentPlatformLiveMode: Signal<boolean> = this._paymentPlatformLiveMode.asReadonly();
    private readonly _walletPaymentProcessor: WritableSignal<WalletPaymentProcessor> = signal<WalletPaymentProcessor>(undefined);
    public readonly walletPaymentProcessor: Signal<WalletPaymentProcessor> = this._walletPaymentProcessor.asReadonly();

    // Tracks the most recently created processor even when its init() didn't find a wallet provider (and
    // so _walletPaymentProcessor above is left undefined) - a background recheck can still be pending on
    // it, and it must be disposed before the next one is created or that recheck loops forever.
    private lastCreatedWalletPaymentProcessor: WalletPaymentProcessor;
    private readonly _requestPayerName: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly requestPayerName: Signal<boolean> = this._requestPayerName.asReadonly();
    private readonly _requestPayerEmail: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly requestPayerEmail: Signal<boolean> = this._requestPayerEmail.asReadonly();
    private readonly _requestPayerPhone: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly requestPayerPhone: Signal<boolean> = this._requestPayerPhone.asReadonly();
    private readonly _requestPayerPostcode: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly requestPayerPostcode: Signal<boolean> = this._requestPayerPostcode.asReadonly();

    // Tracks whether a wallet payment is currently being submitted. This is intended to control the display of spinners.
    private readonly _isSubmittingWalletPayment: WritableSignal<boolean> = signal(false);
    public readonly isSubmittingWalletPayment: Signal<boolean> = this._isSubmittingWalletPayment.asReadonly();

    private readonly _paymentMethods: WritableSignal<string> = signal<string>(undefined);
    public readonly paymentMethods: Signal<string> = this._paymentMethods.asReadonly();

    public readonly showQuickPay: Signal<boolean> = computed<boolean>(() =>
        !this.orderingContextModel.fulfilmentDetailsRequired() && this.quickPayType() !== undefined && this.quickPayType() !== PaymentType.NONE);

    public readonly quickPayType: Signal<PaymentType> = computed<PaymentType>(() => {

        // No quick pay for waiters.
        // Order must be valid and properly initiated.
        // Deliveries require the ability to enter instructions.
        if (this.configurationModel.isWaiterActor() || !this.orderModel.isOrderReadyForPayment() || this.orderingContextModel.orderType() === OrderType.ORDER_DELIVERY) {
            return undefined;
        }

        // Payment via a group order is always available as quick pay unless this is itself a group order
        if (!this.groupOrderModel.currentGroupOrder() && this.orderModel.initiatedOrder() && this.orderModel.initiatedOrder().availableTransferTargets.length > 0) {
            return PaymentType.GROUP_PAY;
        }

        let savedCardQuickPayAvailable: boolean = false;
        let appleQuickPayAvailable: boolean = false;
        let googleQuickPayAvailable: boolean = false;

        // As a member or guest with a stored credit card, we may not have all the required information
        if (this.orderModel.paymentRequired()) {
            if (this.hasSavedCreditCard() &&
                (!this.orderingContextModel.requirePhoneNumber() || this.orderingContextModel.mobilePhone()) &&
                (!this.orderingContextModel.requireEmail() || this.orderingContextModel.email()) &&
                (!this.orderingContextModel.requireFirstName() || this.orderingContextModel.firstName())) {
                savedCardQuickPayAvailable = true;
            }
        }

        // Orders not requiring payment may still require customer details
        else if ((this.orderingContextModel.showPhoneNumber() && !this.orderingContextModel.mobilePhone()) ||
            (this.orderingContextModel.showEmail() && !this.orderingContextModel.email()) ||
            (this.orderingContextModel.showName() && !this.orderingContextModel.firstName())) {
            return undefined;
        }

        // For a wallet payment where quick pay is available, we assume that any required information will be available through the provider.
        if (this._walletPaymentProcessor() && this._walletPaymentProcessor().quickPayAvailable()) {
            if (this.canMakeApplePayPayment()) {
                appleQuickPayAvailable = true;
            }
            if (this.canMakeGooglePayPayment()) {
                googleQuickPayAvailable = true;
            }
        }

        // Preference the use of the last payment type if known.
        // If unknown, preference wallet payments if available.
        if (savedCardQuickPayAvailable) {
            if (appleQuickPayAvailable || googleQuickPayAvailable) {
                const customerDetails: CustomerDetails = this.customerDetailsService.loadCustomer();
                if (appleQuickPayAvailable && (!customerDetails.lastPaymentType || customerDetails.lastPaymentType === PaymentType.APPLE_PAY)) {
                    return PaymentType.APPLE_PAY;
                }
                if (googleQuickPayAvailable && (!customerDetails.lastPaymentType || customerDetails.lastPaymentType === PaymentType.GOOGLE_PAY)) {
                    return PaymentType.GOOGLE_PAY;
                }
            }
            return PaymentType.SAVED_CREDIT_CARD;
        }
        if (appleQuickPayAvailable) {
            return PaymentType.APPLE_PAY;
        }
        if (googleQuickPayAvailable) {
            return PaymentType.GOOGLE_PAY;
        }

        // Quick "no-pay"
        if (this.orderModel.orderTotal() <= 0) {
            return PaymentType.NONE;
        }

        return undefined;
    });

    public readonly canMakeWalletPayment: Signal<boolean> = computed<boolean>(() =>
        this.orderModel.paymentRequired() && !!this._walletPaymentProcessor());

    public readonly canMakeGooglePayPayment: Signal<boolean> = computed<boolean>(() =>
        this.canMakeWalletPayment() && this._walletPaymentProcessor().walletProvider() === WalletProvider.GOOGLE_PAY);

    public readonly canMakeApplePayPayment: Signal<boolean> = computed<boolean>(() =>
        this.canMakeWalletPayment() && this._walletPaymentProcessor().walletProvider() === WalletProvider.APPLE_PAY);

    public readonly canTapToPay: Signal<boolean> = computed<boolean>(() =>
        this.orderModel.paymentRequired() && this._paymentProvider() === IntegratedPaymentProvider.STRIPE && this.tapToPayService.isAvailable());

    public readonly hasSavedCreditCard: Signal<boolean> = computed<boolean>(() =>
        !!this.orderPaymentDetailsModel.dataSignal()?.cardLast4 && this.customerDetailsService.hasSavedPaymentCustomer());

    public readonly cardBrand: Signal<string> = computed<string>(() =>
        this.orderPaymentDetailsModel.dataSignal()?.cardBrand);

    public readonly cardCountry: Signal<string> = computed<string>(() =>
        this.orderPaymentDetailsModel.dataSignal()?.cardCountry);

    public readonly cardLast4: Signal<string> = computed<string>(() =>
        this.orderPaymentDetailsModel.dataSignal()?.cardLast4);

    /**
     * Notifies that payment details have been applied.
     */
    public readonly paymentDetailsApplied$: Subject<void> = new Subject();

    private paymentPlatformAccountId: string;
    private forcePaymentActivationError: boolean;
    private readonly walletPaymentProcessorCreationRequested$: Subject<void> = new Subject<void>();
    private readonly walletPaymentProcessorCreationResolved$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor() {
        console.log("--- PaymentModel");

        onEach(this.walletPaymentProcessorCreationRequested$.pipe(
            debounceTime(1000)
        ), () => this.createWalletPaymentProcessor());

        onEach(this.orderingContextModel.storeDetailsChanged$, async (storeChanged: boolean) => storeChanged && this.loadCustomerPaymentDetails("Store changed"));

        effect(() => {
            if (this.configurationModel.app()) {
                this.requestWalletPaymentProcessorCreation("App changed");
            }
        });
        effectOnSignalSet(this.orderingContextModel.orderType, () => this.requestWalletPaymentProcessorCreation("Order type changed"));
        effectOnSignalSet(this.orderingContextModel.marketingOptIn, () => this.requestWalletPaymentProcessorCreation("Marketing opt-in changed"));

        // For tyro, an order initiation from the payment page will create a new payment secret - the old one must be discarded.
        effect(() => {
            if (this.orderModel.orderInitiationStatus() === OrderInitiationStatus.INITIATING && this._paymentProvider() === IntegratedPaymentProvider.TYRO && this.sessionModel.orderingPhase() === OrderingPhase.PAYMENT) {
                this._paymentPlatformPublicKey.set(undefined);
            }
        });

        (async () => {
            this.forcePaymentActivationError = (await this.configurationModel.getParamConfig()).forcePaymentActivationError;
            if (this.forcePaymentActivationError) {
                document.addEventListener(PaymentModel.SHOW_WALLET_PAYMENT_SHEET_EVENT, (event: CustomEvent) => this._walletPaymentProcessor().showPaymentSheet(event.detail.amount));
            }
        })();
    }

    public inject(): void {
        this.orderModel = inject(OrderModel);
        this.groupOrderModel = inject(GroupOrderModel);
    }

    public async loadCustomerPaymentDetails(reason: string): Promise<void> {
        console.log("loading customer payment details:", reason);
        try {
            // Load payment details without a customer id first.  This will determine the customer scope (provider and live-mode setting).
            await this.orderPaymentDetailsModel.loadEntity(null);
            this.customerDetailsService.setPaymentScope(this.orderPaymentDetailsModel.dataSignal());

            // Find a persisted customer
            const paymentCustomerDetails: PaymentCustomerDetails = this.customerDetailsService.loadPaymentCustomer();

            // If we have a customer, load the payment details again to get the card details.
            if (paymentCustomerDetails) {
                await this.orderPaymentDetailsModel.loadEntity(paymentCustomerDetails.paymentCustomerId);
            }

            this.applyPaymentDetails(this.orderPaymentDetailsModel.dataSignal());
        } catch (error) {
            void this.notificationService.displayFooterError(error, "woa.error.common.cannotCommunicateServer");
            throw error;
        }

        this.requestWalletPaymentProcessorCreation(reason);
    }

    public applyPaymentDetails(paymentDetails: OrderPaymentDetailsData) {
        let changed: boolean = false;
        if (paymentDetails.platformPublicKey && paymentDetails.platformPublicKey !== this._paymentPlatformPublicKey()) {
            this._paymentPlatformPublicKey.set(paymentDetails.platformPublicKey);
            changed = true;
        }
        if (paymentDetails.provider && paymentDetails.provider !== this._paymentProvider()) {
            this._paymentProvider.set(paymentDetails.provider);
            changed = true;
        }
        if (paymentDetails.platformAccountId != undefined && paymentDetails.platformAccountId !== this.paymentPlatformAccountId) {
            this.paymentPlatformAccountId = paymentDetails.platformAccountId;
            changed = true;
        }
        if (paymentDetails.platformLiveMode != undefined && paymentDetails.platformLiveMode !== this._paymentPlatformLiveMode()) {
            this._paymentPlatformLiveMode.set(paymentDetails.platformLiveMode);
            changed = true;
        }
        if (paymentDetails.paymentMethods != undefined && paymentDetails.paymentMethods !== this._paymentMethods()) {
            this._paymentMethods.set(paymentDetails.paymentMethods);
            changed = true;
        }
        if (changed) {
            this.paymentDetailsApplied$.next();
            this.requestWalletPaymentProcessorCreation("New payment details");
        } else {
            this.walletPaymentProcessorCreationResolved$.next(true);
        }
    }

    public canTip(paymentType: PaymentType): boolean {
        return paymentType === PaymentType.SAVED_CREDIT_CARD || paymentType === PaymentType.NEW_CREDIT_CARD ||
            paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY ||
            paymentType === PaymentType.TAP_TO_PAY;
    }

    public discardCard(): void {
        this.orderPaymentDetailsModel.data.cardLast4 = undefined;
    }

    private requestWalletPaymentProcessorCreation(reason: string): void {
        console.log(Date.now(), "Wallet payment processor creation requested:", reason);
        this.walletPaymentProcessorCreationResolved$.next(false);
        this.walletPaymentProcessorCreationRequested$.next();
    }

    private async createWalletPaymentProcessor(): Promise<void> {
        console.log("Creating wallet payment processor");
        this._requestPayerName.set(!this.orderingContextModel.firstNameTrusted() && (this.orderingContextModel.showName() || this.orderingContextModel.marketingOptIn()));
        this._requestPayerEmail.set(!this.orderingContextModel.emailTrusted() && (this.orderingContextModel.showEmail() || (this.orderingContextModel.marketingOptIn() && this.configurationModel.app()?.collectMarketingEmail)));
        this._requestPayerPhone.set(!this.orderingContextModel.mobilePhoneTrusted() && (this.orderingContextModel.showPhoneNumber() || (this.orderingContextModel.marketingOptIn() && this.configurationModel.app()?.collectMarketingMobilePhone)));
        this._requestPayerPostcode.set(this.orderingContextModel.marketingOptIn() && this.configurationModel.app()?.collectMarketingPostCode);

        this.lastCreatedWalletPaymentProcessor?.dispose();

        let processor: WalletPaymentProcessor;
        try {
            await this.loadProviderScripts(this._paymentProvider());
            processor =
                this._paymentProvider() === IntegratedPaymentProvider.STRIPE ? new StripeWalletPaymentProcessor(this, this.configurationModel) :
                    this._paymentProvider() === IntegratedPaymentProvider.TYRO ? new TyroWalletPaymentProcessor(this, this.configurationModel) :
                        this._paymentProvider() === IntegratedPaymentProvider.OOLIO ? new AdyenWalletPaymentProcessor(this, this.configurationModel) :
                            undefined;
        } catch (error) {
            console.error("Failed to load wallet payment provider script(s)", error);
            processor = undefined;
        }
        this.lastCreatedWalletPaymentProcessor = processor;
        this._walletPaymentProcessor.set(processor ? await processor.init() : undefined);

        this.walletPaymentProcessorCreationResolved$.next(true);

        void this.analyticsService.trackWalletPaymentStatus(!!this._walletPaymentProcessor());
    }

    private stripeReady: Promise<void>;

    /**
     * Loads the external script(s) required by the given payment provider.
     * Tyro has no native mechanism to detect wallet payment availability, so it also relies on Stripe's API for that - see TyroWalletPaymentProcessor.
     */
    private async loadProviderScripts(provider: IntegratedPaymentProvider): Promise<void> {
        switch (provider) {
            case IntegratedPaymentProvider.STRIPE:
                await this.ensureStripeReady();
                return;
            case IntegratedPaymentProvider.TYRO:
                await Promise.all([
                    this.externalScriptLoaderService.loadScript(TYRO_SCRIPT_URL),
                    this.ensureStripeReady()
                ]);
                return;
            case IntegratedPaymentProvider.OOLIO:
                await Promise.all([
                    this.externalScriptLoaderService.loadScript(ADYEN_SCRIPT_URL, {integrity: ADYEN_SCRIPT_INTEGRITY, crossorigin: "anonymous"}),
                    this.externalScriptLoaderService.loadStylesheet(ADYEN_STYLESHEET_URL, {integrity: ADYEN_STYLESHEET_INTEGRITY, crossorigin: "anonymous"})
                ]);
                return;
            default:
                return;
        }
    }

    /**
     * Stripe's script executing doesn't guarantee the browser/OS's own wallet discovery (Google Pay/Apple
     * Pay eligibility) has finished yet - calling canMakePayment() immediately after can observe a false
     * negative. (Chrome's canMakePayment() has been reported elsewhere to cache a negative result for a
     * while, which would make an early false negative sticky - that specific caching behaviour wasn't
     * reproduced in testing here though, so treat it as unconfirmed for this app.) Only the very first
     * load needs this margin - loadScript's own per-URL caching means later calls resolve immediately, so
     * this is cached the same way rather than paying it on every provider-script load.
     */
    private ensureStripeReady(): Promise<void> {
        if (!this.stripeReady) {
            this.stripeReady = this.externalScriptLoaderService.loadScript(STRIPE_SCRIPT_URL).then(() =>
                new Promise<void>((resolve: () => void) =>
                    "requestIdleCallback" in window ? requestIdleCallback(() => resolve(), {timeout: 1000}) : setTimeout(resolve, 1)));
        }
        return this.stripeReady;
    }

    /**
     * Allows the caller to wait for the wallet payment processor creation to be resolved.
     */
    public async untilWalletPaymentProcessorCreationResolved(): Promise<void> {
        await whenObservableMatches(this.walletPaymentProcessorCreationResolved$, resolved => resolved === true);
    }

    /**
     * Submits the payment request and returns a payment authorisation.
     *
     * @param paymentType Determines how the card details are submitted.
     * @param paymentCard The card details to submit if paymentType is NEW_CREDIT_CARD.
     * @param paymentAuthorisation Provided if this is a retry - this will be the authorisation returned from this method on the first attempt.
     * @param cardValidationFailed Called if the provider rejects the card details during submission.
     */
    public async authorisePayment(paymentType: PaymentType, paymentCard: PaymentCard, paymentAuthorisation: PaymentAuthorisationData, cardValidationFailed: () => void): Promise<PaymentAuthorisationData> {

        // Nothing to do if this is a 3DS or error retry
        if (paymentAuthorisation && paymentAuthorisation.isReusable) {
            return paymentAuthorisation;
        }

        if (!paymentAuthorisation) {
            paymentAuthorisation = <PaymentAuthorisationData>EntityData.create(PaymentAuthorisationData);
            paymentAuthorisation.paymentRequestId = uuid();
            paymentAuthorisation.paymentType = paymentType;
            paymentAuthorisation.storePaymentDetails = paymentType === PaymentType.NEW_CREDIT_CARD && paymentCard.canSavePaymentDetails();

            paymentAuthorisation.integratedPaymentConfigId = this.orderModel.initiatedOrder().paymentDetails.integratedPaymentConfigId;
            paymentAuthorisation.platformLiveMode = this._paymentPlatformLiveMode();
            paymentAuthorisation.platformAccountId = this.paymentPlatformAccountId;
            paymentAuthorisation.platformApplicationFee = this.orderModel.initiatedOrder().paymentDetails.platformApplicationFee;
            paymentAuthorisation.orderRequestId = this.orderModel.initiatedOrder().requestId;

            const paymentCustomerDetails: PaymentCustomerDetails = (this.configurationModel.isWaiterActor() || paymentType === PaymentType.GROUP_PAY) ? undefined : this.customerDetailsService.loadPaymentCustomer();
            if (paymentCustomerDetails) {
                paymentAuthorisation.paymentCustomerId = paymentCustomerDetails.paymentCustomerId;
                paymentAuthorisation.paymentCustomerCreated = paymentCustomerDetails.created;
            }

            // Amount without surcharges and tips
            paymentAuthorisation.amount = this.orderModel.initiatedOrder().orderTotal;

            if (paymentType === PaymentType.POINTS) {
                paymentAuthorisation.payWithPoints = true;
            } else {
                const paymentSurcharge: PaymentSurchargeData = this.orderModel.initiatedOrder().getPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE);
                paymentAuthorisation.paymentSurchargeAmount = paymentSurcharge ? paymentSurcharge.amount : undefined;

                // For tyro, the tip amount has to match what was sent with the order initiation, since that's when the payment intent is created.
                paymentAuthorisation.tipAmount = this._paymentProvider() === IntegratedPaymentProvider.TYRO ? this.orderModel.initiatedOrder().tipAmount : this.orderModel.tipAmount();
            }

            if (paymentType === PaymentType.SAVED_CREDIT_CARD) {
                paymentAuthorisation.paymentCardBrand = this.cardBrand();
                paymentAuthorisation.paymentCardCountry = this.cardCountry();
                paymentAuthorisation.paymentCardLast4 = this.cardLast4();
            } else {
                paymentAuthorisation.paymentIntentId = this.orderModel.initiatedOrder().paymentDetails.paymentIntentId;
            }

            // Browser info for 3DS (required for Adyen stored card payments)
            paymentAuthorisation.origin = window.location.origin;
            paymentAuthorisation.browserInfo.acceptHeader = "*/*";
            paymentAuthorisation.browserInfo.colorDepth = screen.colorDepth;
            paymentAuthorisation.browserInfo.javaEnabled = false;
            paymentAuthorisation.browserInfo.javaScriptEnabled = true;
            paymentAuthorisation.browserInfo.language = navigator.language;
            paymentAuthorisation.browserInfo.screenHeight = screen.height;
            paymentAuthorisation.browserInfo.screenWidth = screen.width;
            paymentAuthorisation.browserInfo.timeZoneOffset = new Date().getTimezoneOffset();
            paymentAuthorisation.browserInfo.userAgent = navigator.userAgent;
        }

        switch (paymentType) {
            case PaymentType.TAP_TO_PAY:
                try {
                    const authorisedPayment: AuthorisedTapToPayPayment = await this.tapToPayService.authorisePayment(paymentAuthorisation);
                    paymentAuthorisation.paymentAuthorisationType = PaymentAuthorisationType.STRIPE_PAYMENT_INTENT;
                    paymentAuthorisation.paymentIntentId = authorisedPayment.paymentIntentId;
                    paymentAuthorisation.paymentCardBrand = authorisedPayment.paymentCardBrand;
                    paymentAuthorisation.paymentCardCountry = authorisedPayment.paymentCardCountry;
                    paymentAuthorisation.paymentCardLast4 = authorisedPayment.paymentCardLast4;
                    paymentAuthorisation.paymentCardNetwork = authorisedPayment.paymentCardNetwork;
                } catch (error) {
                    this.loadingService.dismissSpinner();
                    if (error) {
                        console.error("Tap-to-pay error", JSON.stringify(error));
                    }

                    void this.notificationService.displayAlert(null,
                        !error ? "woa.error.payment.cannotProcess"
                            : !isAppDeviceError(error) ? "woa.error.payment.cannotProcessDueToError"
                                : error.errorType === AppDeviceErrorType.PAYMENT_CANCELLED ? "woa.error.payment.tapToPayCancelled"
                                    : error.errorType === AppDeviceErrorType.HANDLER_FAILURE ? "woa.error.payment.cannotProcessDueToInternalError"
                                        : error.errorType === AppDeviceErrorType.CHEWZIE_ERROR ? "woa.error.payment.cannotProcessDueToInternalError"
                                            : error.errorType === AppDeviceErrorType.PROVIDER_ERROR ? "woa.error.payment.providerError"
                                                : "woa.error.payment.cannotProcess",
                        {
                            "error": error,
                            "errorMessage": isAppDeviceError(error) ? (error.errorMessage || " ") : undefined,
                            "errorCode": isAppDeviceError(error) ? (error.errorCode || " ") : undefined
                        });

                    // Error is not rethrown - failures are expected due to cancellation of the payment, etc., and are handled above.
                    return undefined;
                }
                break;
            case PaymentType.NEW_CREDIT_CARD:
                try {
                    const submissionResult: CardPaymentSubmissionResult | CardPaymentSubmissionFailure = await paymentCard.submit();
                    if (!submissionResult) {
                        return undefined;
                    }
                    if (submissionResult instanceof CardPaymentSubmissionFailure) {
                        this.loadingService.dismissSpinner();
                        if (submissionResult.cardValidationFailure && cardValidationFailed) {
                            cardValidationFailed();
                        }
                        if (submissionResult.notifiable) {
                            void this.analyticsService.trackPlaceOrderFailed(submissionResult.errorMessage, submissionResult.errorCode, submissionResult.gatewayCode);
                            this.notificationService.logErrorToServer("OrderModel.placeOrder - payment authorisation failed", undefined, submissionResult);
                            await this.notificationService.displayAlert("woa.message.order.paymentAuthorisationFailed", "woa.message.order.paymentAuthorisationFailedReason", {reason: submissionResult.errorMessage});
                        }
                        this.orderModel.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
                        return undefined;
                    }

                    // Discard the Tyro pay secret after use
                    if (this._paymentProvider() === IntegratedPaymentProvider.TYRO) {
                        this._paymentPlatformPublicKey.set(undefined);
                    }

                    paymentAuthorisation.paymentAuthorisationType = submissionResult.authorisationType;
                    paymentAuthorisation.token = submissionResult.token;
                    paymentAuthorisation.isReusable = true;
                    paymentAuthorisation.paymentCardBrand = submissionResult.brand;
                    paymentAuthorisation.paymentCardCountry = submissionResult.country;
                    paymentAuthorisation.paymentCardLast4 = submissionResult.last4;
                } catch (error) {
                    this.loadingService.dismissSpinner();
                    void this.notificationService.displayAlert(null, error ? "woa.error.payment.cannotProcessDueToError" : "woa.error.payment.cannotProcess", {"error": error});

                    // Rethrow error since errors here are unexpected and should be logged as an anomaly.
                    // noinspection ExceptionCaughtLocallyJS
                    throw error;
                }
                break;
            case PaymentType.APPLE_PAY:
            case PaymentType.GOOGLE_PAY:
                this._isSubmittingWalletPayment.set(true);

                let showWalletPaymentPage: boolean = !this._walletPaymentProcessor().canSubmit();

                // Retry loop for transient user activation error
                while (true) {
                    try {
                        const submissionResult: WalletPaymentSubmissionResult = await (showWalletPaymentPage
                            ? this.navigationService.createPage("woa-wallet-payment-page")
                            : this._walletPaymentProcessor().submit(this.orderModel.orderTotalPayment(), this.forcePaymentActivationError));
                        if (!submissionResult) {
                            return undefined;
                        }
                        paymentAuthorisation.paymentAuthorisationType = submissionResult.authorisationType;
                        paymentAuthorisation.token = submissionResult.token;
                        paymentAuthorisation.isReusable = true;

                        const payerFullName: string = submissionResult.payerName && submissionResult.payerName.trim();
                        if (this._requestPayerName() && payerFullName) {
                            console.log("Received payer name from wallet payment:", payerFullName);
                            const names: string[] = payerFullName.split(" ");
                            if (names.length) {
                                const payerFirstName: string = names[0].trim().substring(0, 30);
                                if (payerFirstName) {
                                    this.orderingContextModel.firstName.set(payerFirstName);
                                    this.orderingContextModel.firstNameTrusted.set(true);
                                }
                                if (names.length > 1) {
                                    const i: number = payerFullName.indexOf(" ") + 1;
                                    const payerLastName: string = payerFullName.trim().substring(i, i + 30);
                                    if (payerLastName) {
                                        this.orderingContextModel.lastName.set(payerLastName);
                                        this.orderingContextModel.lastNameTrusted.set(true);
                                    }
                                }
                            }
                        }

                        const payerEmail: string = submissionResult.payerEmail && submissionResult.payerEmail.trim();
                        if (this._requestPayerEmail() && payerEmail) {
                            console.log("Received payer email from wallet payment:", payerEmail);
                            this.orderingContextModel.email.set(payerEmail);
                            this.orderingContextModel.emailTrusted.set(true);
                        }

                        const payerPhone: string = submissionResult.payerPhone && submissionResult.payerPhone.trim();
                        if (this._requestPayerPhone() && payerPhone) {
                            console.log("Received payer phone from wallet payment:", payerPhone);

                            // Phone number may require confirmation, so we don't place it into the ordering context, but return it in the payment authorisation.
                            paymentAuthorisation.payerPhone = payerPhone;
                        }

                        const payerPostcode: string = submissionResult.payerPostcode && submissionResult.payerPostcode.trim();
                        if (this._requestPayerPostcode() && payerPostcode) {
                            console.log("Received payer postcode from wallet payment:", payerPostcode);

                            // This is only captured for opt-in marketing. Never use it instead of the actual postcode already on the address.
                            if (!this.orderingContextModel.address().postCode) {
                                this.orderingContextModel.address().postCode = payerPostcode;
                            }
                        }

                        // Discard the Tyro pay secret after use
                        if (this._paymentProvider() === IntegratedPaymentProvider.TYRO) {
                            this._paymentPlatformPublicKey.set(undefined);
                        }
                    } catch (error) {
                        if (error) {
                            if (!showWalletPaymentPage && error instanceof WalletPaymentSubmissionFailure &&
                                (error.errorType === WalletPaymentErrorType.PROVIDER_NOT_READY || error.errorType === WalletPaymentErrorType.TRANSIENT_ACTIVATION)) {

                                if (error.errorType === WalletPaymentErrorType.PROVIDER_NOT_READY) {
                                    void this.analyticsService.trackWalletPaymentProviderNotReadyError();
                                } else {
                                    void this.analyticsService.trackWalletPaymentTransientActivationError();
                                }

                                showWalletPaymentPage = true;
                                await this._walletPaymentProcessor().init();
                                continue;
                            }

                            console.error("Wallet payment error", error);
                        }

                        void this.notificationService.displayAlert(null,
                            !error ? "woa.error.payment.cannotProcess"
                                : !(error instanceof WalletPaymentSubmissionFailure) ? "woa.error.payment.cannotProcessDueToError"
                                    : error.errorType === WalletPaymentErrorType.APPLE_PAY_CANCELLED ? "woa.error.payment.applePayCancelled"
                                        : error.errorType === WalletPaymentErrorType.GOOGLE_PAY_CANCELLED ? "woa.error.payment.googlePayCancelled"
                                            : error.errorType === WalletPaymentErrorType.PROVIDER_ERROR ? "woa.error.payment.providerError"
                                                : "woa.error.payment.cannotProcess",
                            {
                                "error": error,
                                "errorMessage": error instanceof WalletPaymentSubmissionFailure ? (error.providerErrorMessage || " ") : undefined,
                                "errorCode": error instanceof WalletPaymentSubmissionFailure ? (error.providerErrorCode || " ") : undefined
                            });

                        // Error is not rethrown - failures are expected due to cancellation of the payment, etc., and are handled above.
                        return undefined;
                    } finally {
                        this._isSubmittingWalletPayment.set(false);
                    }
                    break;
                }
                break;
        }
        return paymentAuthorisation;
    }

    public showWalletPaymentSheetDeferred(amount: number) {
        setTimeout(() => document.dispatchEvent(new CustomEvent(PaymentModel.SHOW_WALLET_PAYMENT_SHEET_EVENT, {detail: {amount: amount}})), 5000);
    }

    public async handle3DS(action: any, paymentAuthorisation: PaymentAuthorisationData): Promise<ThreeDSResult> {
        let threeDSResult: ThreeDSResult;
        switch (this._paymentProvider()) {
            case IntegratedPaymentProvider.STRIPE:
                threeDSResult = await this.loadingService.performWithSpinner(async () => {
                    // noinspection JSUnresolvedReference
                    const result: any = await new (<any>window).Stripe(this._paymentPlatformPublicKey()).handleCardAction(action["paymentIntentClientSecret"]);
                    return new ThreeDSResult(!result.error, action["paymentIntentId"], result.error);
                }, 900000);
                break;
            case IntegratedPaymentProvider.OOLIO:
                threeDSResult = await this.navigationService.createPage("woa-adyen-3ds-page", {action: action}) ||
                    new ThreeDSResult(false, undefined, "User cancelled 3DS challenge");
                break;
            default:
                throw "3DS not supported for " + this._paymentProvider();
        }
        if (threeDSResult.success) {
            // Retry the order placement using the 3DS intent rather than getting a new token
            paymentAuthorisation.isReusable = true;
            paymentAuthorisation.isThreeDS = true;
            paymentAuthorisation.paymentIntentId = threeDSResult.paymentIntent;
            paymentAuthorisation.token = undefined;
        }
        return threeDSResult;
    }

    public completePayment(paymentAuthorisation: PaymentAuthorisationData): void {
        if (paymentAuthorisation.isThreeDS) {
            void this.analyticsService.track3DSSucceeded(this.orderModel.orderTotalPayment());
        }

        // Save the payment customer id in the browser local storage.
        if (paymentAuthorisation.paymentType === PaymentType.NEW_CREDIT_CARD) {

            // Update or remove the locally persisted payment customer id.
            if (paymentAuthorisation.storePaymentDetails) {
                let paymentCustomerDetails: PaymentCustomerDetails = this.customerDetailsService.loadPaymentCustomer();
                if (!paymentCustomerDetails || paymentCustomerDetails.paymentCustomerId !== this.orderModel.placedOrder().paymentCustomerId) {
                    paymentCustomerDetails = new PaymentCustomerDetails();
                    paymentCustomerDetails.paymentCustomerId = this.orderModel.placedOrder().paymentCustomerId;
                    paymentCustomerDetails.created = new Date();
                    this.customerDetailsService.savePaymentCustomer(paymentCustomerDetails);
                }
            } else {
                this.customerDetailsService.removePaymentCustomer();
            }

            // Once a payment has been completed using a new credit card, the model's copy of the partial card number is discarded.
            // This prevents it from being presented for use prior to payment details being reloaded to reflect the new (or removed) card.
            this.discardCard();
        }
    }
}
